// src/components/analytics/AnalyticsBookingFlow.tsx
import React, { useMemo } from 'react';
import { CalendarClock, TrendingUp } from 'lucide-react';

type Props = {
    bookings: any[];
};

// getDay(): 0=Dom..6=Sab. Ordine di visualizzazione: Lun-first.
const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // getDay index nell'ordine Lun..Dom

const AnalyticsBookingFlow: React.FC<Props> = ({ bookings }) => {
    const flow = useMemo(() => {
        const DAY = 86400000;
        let leadSum = 0;
        let leadCount = 0;
        const weekdayCounts = [0, 0, 0, 0, 0, 0, 0]; // per getDay()

        bookings.forEach((b) => {
            if (!b?.created_at || !b?.date) return;
            const created = new Date(b.created_at);
            const appt = new Date(b.date);
            if (isNaN(created.getTime()) || isNaN(appt.getTime())) return;

            const createdDay = new Date(created);
            createdDay.setHours(0, 0, 0, 0);
            const apptDay = new Date(appt);
            apptDay.setHours(0, 0, 0, 0);
            const days = Math.round((apptDay.getTime() - createdDay.getTime()) / DAY);
            if (days >= 0 && days < 365) {
                leadSum += days;
                leadCount++;
            }
            weekdayCounts[created.getDay()]++;
        });

        const avgLead = leadCount ? leadSum / leadCount : 0;
        const totalWeekday = weekdayCounts.reduce((a, b) => a + b, 0);
        const maxWeekday = Math.max(...weekdayCounts, 1);
        const topWeekdayIdx = weekdayCounts.indexOf(Math.max(...weekdayCounts));
        const topWeekdayLabel =
            totalWeekday > 0 ? WEEKDAY_LABELS[DISPLAY_ORDER.indexOf(topWeekdayIdx)] : null;

        return { avgLead, weekdayCounts, maxWeekday, totalWeekday, topWeekdayLabel };
    }, [bookings]);

    return (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8">
            <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-clip-50 text-clip-500 flex items-center justify-center">
                    <TrendingUp size={20} />
                </div>
                <div>
                    <h3 className="text-lg font-bold text-gray-900 leading-none">Flusso di prenotazione</h3>
                    <p className="text-sm text-gray-400 mt-1">Come e quando prenotano i tuoi clienti</p>
                </div>
            </div>

            {flow.totalWeekday === 0 ? (
                <p className="text-sm text-gray-400">Nessun dato di prenotazione nel periodo selezionato.</p>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-3 mb-6">
                        <div className="rounded-2xl border border-gray-100 p-4">
                            <div className="flex items-center gap-1.5 text-clip-500 text-xs font-bold uppercase tracking-wider">
                                <CalendarClock size={14} /> Anticipo medio
                            </div>
                            <p className="text-2xl font-bold text-gray-900 mt-1">
                                {flow.avgLead < 1 ? 'Stesso giorno' : `${Math.round(flow.avgLead)} gg`}
                            </p>
                            <p className="text-xs text-gray-400">prima dell'appuntamento</p>
                        </div>
                        <div className="rounded-2xl border border-gray-100 p-4">
                            <div className="text-xs font-bold uppercase tracking-wider text-gray-400">Prenotano di più</div>
                            <p className="text-2xl font-bold text-gray-900 mt-1">{flow.topWeekdayLabel ?? '—'}</p>
                            <p className="text-xs text-gray-400">giorno tipico di prenotazione</p>
                        </div>
                    </div>

                    <div className="flex items-end justify-between gap-2 h-28">
                        {DISPLAY_ORDER.map((dayIdx, i) => {
                            const count = flow.weekdayCounts[dayIdx];
                            const heightPct = Math.round((count / flow.maxWeekday) * 100);
                            return (
                                <div key={i} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
                                    <div
                                        className="w-full rounded-t-lg bg-clip-500/80 transition-all"
                                        style={{ height: `${Math.max(heightPct, 4)}%` }}
                                        title={`${count} prenotazioni`}
                                    />
                                    <span className="text-[10px] font-semibold text-gray-400">{WEEKDAY_LABELS[i]}</span>
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-3 text-center">
                        Distribuzione dei giorni in cui i clienti effettuano la prenotazione
                    </p>
                </>
            )}
        </div>
    );
};

export default AnalyticsBookingFlow;
